/**
 * The sign-in screen.
 *
 * One detail is worth explaining: the organization field appears only when the
 * server says it is needed. A username can exist in several organizations, and the
 * API answers such a login with `409 ambiguous_login` plus the list — so the form
 * starts simple and grows a picker at the exact moment the ambiguity is real,
 * rather than asking everyone for a field most people do not have to think about.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import { Button, TextInput } from '../components/common';
import { useAuth } from '../context/AuthContext';

export function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [organization, setOrganization] = useState('');
  const [choices, setChoices] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Sign in · iRAP VLM Studio';
  }, []);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        await login(username.trim(), password, organization || undefined);
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === 'ambiguous_login') {
          const options = caught.details?.organizations ?? [];
          setChoices(options);
          setOrganization(options[0]?.slug ?? '');
          setError(
            'That username exists in more than one organization. Choose which one, ' +
              'then sign in again.',
          );
        } else {
          setError(caught.message);
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, login, username, password, organization],
  );

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <header className="login__header">
          <div className="login__mark" aria-hidden="true">
            iR
          </div>
          <div>
            <h1 className="login__title">iRAP VLM Studio</h1>
            <p className="login__subtitle">Road attribute coding from survey imagery</p>
          </div>
        </header>

        <TextInput
          label="Username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          autoFocus
          required
        />

        <TextInput
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />

        {choices.length > 0 && (
          <label className="field">
            <span className="field__label">Organization</span>
            <select
              className="input"
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
            >
              {choices.map((choice) => (
                <option key={choice.slug} value={choice.slug}>
                  {choice.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && (
          <p className="login__error" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" disabled={busy} className="login__submit">
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>

        <p className="login__foot">No account yet? Contact iRAP to request one.</p>
      </form>
    </div>
  );
}
